import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, ChevronDown, ChevronRight, ToggleLeft, ToggleRight,
  Smartphone, Eye, EyeOff, GripVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("it_token") || ""}` } });

const ICON_OPTIONS = [
  "Package","Heart","Tag","HelpCircle","MapPin","Bell","Star","MessageCircle",
  "Shield","Globe","Store","FileText","User","Settings","Wallet","RotateCcw",
  "Gift","Eye","Truck","LogOut","ChevronRight","Home","Search","Phone",
  "Mail","ShoppingCart","Crown","Zap","Grid","BarChart2",
];

function Toggle({ value, onChange, size = "sm" }) {
  return (
    <button onClick={() => onChange(!value)} type="button"
      className={`relative inline-flex flex-shrink-0 transition-colors rounded-full ${
        size === "sm" ? "w-8 h-4" : "w-11 h-6"
      } ${value ? "bg-orange-500" : "bg-slate-300"}`}>
      <span className={`inline-block bg-white rounded-full shadow transition-transform ${
        size === "sm" ? "w-3.5 h-3.5 translate-y-[1px] translate-x-[1px]" : "w-5 h-5 translate-y-[2px] translate-x-[2px]"
      } ${value ? (size === "sm" ? "translate-x-[17px]" : "translate-x-5") : ""}`} />
    </button>
  );
}

function ItemRow({ item, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-white hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(v => !v)}>
        <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
        <Toggle value={item.enabled} onChange={v => onUpdate({ ...item, enabled: v })} />
        <span className={`flex-1 text-sm font-medium ${item.enabled ? "text-slate-800" : "text-slate-400 line-through"}`}>{item.label || "(unnamed)"}</span>
        <span className="text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{item.icon || "—"}</span>
        <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
      </div>

      {open && (
        <div className="border-t border-slate-100 px-3 py-3 bg-slate-50 grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Label</label>
            <input value={item.label || ""} onChange={e => onUpdate({ ...item, label: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Icon</label>
            <select value={item.icon || ""} onChange={e => onUpdate({ ...item, icon: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400 bg-white">
              <option value="">— none —</option>
              {ICON_OPTIONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">URL / Path</label>
            <input value={item.url || ""} onChange={e => onUpdate({ ...item, url: e.target.value })}
              placeholder="/account or https://..."
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Tab (account sub-page)</label>
            <input value={item.tab || ""} onChange={e => onUpdate({ ...item, tab: e.target.value })}
              placeholder="orders / settings / coupons..."
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-500 block mb-1">Subtitle (optional)</label>
            <input value={item.subtitle || ""} onChange={e => onUpdate({ ...item, subtitle: e.target.value })}
              placeholder="Small description below label"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400" />
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ section, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);

  const updateItem = (idx, item) => onUpdate({ ...section, items: section.items.map((it, i) => i === idx ? item : it) });
  const deleteItem = (idx) => onUpdate({ ...section, items: section.items.filter((_, i) => i !== idx) });
  const addItem = () => onUpdate({ ...section, items: [...(section.items || []), { id: `item_${Date.now()}`, label: "New Item", icon: "ChevronRight", url: "#", tab: "", subtitle: "", enabled: true }] });

  const SHOW_WHEN_OPTIONS = [
    { value: "always",     label: "Always" },
    { value: "logged_in",  label: "Logged In Only" },
    { value: "logged_out", label: "Logged Out Only" },
  ];

  return (
    <div className={`border rounded-xl overflow-hidden ${section.enabled ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
      <div className="flex items-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 cursor-pointer" onClick={() => setOpen(v => !v)}>
        <Toggle value={section.enabled} onChange={v => onUpdate({ ...section, enabled: v })} />
        <span className="flex-1 text-sm font-bold text-slate-800">{section.title || "(untitled section)"}</span>
        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{section.show_when || "always"}</span>
        <span className="text-xs text-slate-400">{(section.items || []).length} items</span>
        <button onClick={e => { e.stopPropagation(); onDelete(); }} className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Section Title</label>
              <input value={section.title || ""} onChange={e => onUpdate({ ...section, title: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400 bg-white" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Show When</label>
              <select value={section.show_when || "always"} onChange={e => onUpdate({ ...section, show_when: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:border-orange-400 bg-white">
                {SHOW_WHEN_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Items</p>
          <div className="space-y-2">
            {(section.items || []).map((item, idx) => (
              <ItemRow key={item.id || idx} item={item} onUpdate={updated => updateItem(idx, updated)} onDelete={() => deleteItem(idx)} />
            ))}
          </div>
          <button onClick={addItem}
            className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 border border-orange-300 bg-orange-50 hover:bg-orange-100 px-3 py-1.5 rounded-lg transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add Item
          </button>
        </div>
      )}
    </div>
  );
}

export default function MobileAccountSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [activeTab, setActiveTab] = useState("sections");

  useEffect(() => {
    axios.get(`${API}/admin/mobile-account-settings`, AUTH())
      .then(r => setCfg(r.data))
      .catch(() => toast.error("Failed to load settings"));
  }, []);

  const mark = (next) => { setCfg(next); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/mobile-account-settings`, cfg, AUTH());
      toast.success("Mobile account settings saved!");
      setDirty(false);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addSection = () => mark({
    ...cfg,
    sections: [...(cfg.sections || []), {
      id: `section_${Date.now()}`, title: "New Section", enabled: true, show_when: "always",
      items: [{ id: `item_${Date.now()}`, label: "New Item", icon: "ChevronRight", url: "#", tab: "", subtitle: "", enabled: true }],
    }],
  });

  if (!cfg) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" /></div>;

  const TABS = [
    { id: "sections",     label: "Sections & Items" },
    { id: "quick",        label: "Quick Actions Grid" },
    { id: "login_prompt", label: "Login Prompt" },
    { id: "login_page",   label: "Login Page Text" },
  ];

  return (
    <div>
      {/* Title + save */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-black text-slate-900">Mobile Account Page</h1>
            <p className="text-xs text-slate-400">Configure sections, items, and text for the mobile account page</p>
          </div>
        </div>
        <button onClick={save} disabled={saving || !dirty}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-colors"
          data-testid="save-mobile-account-settings-btn">
          {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl mb-5 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-all ${
              activeTab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Sections Tab */}
      {activeTab === "sections" && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Sections appear on the mobile account page. Set "Show When" to control visibility.</p>
          {(cfg.sections || []).map((sec, idx) => (
            <SectionCard
              key={sec.id || idx}
              section={sec}
              onUpdate={updated => mark({ ...cfg, sections: cfg.sections.map((s, i) => i === idx ? updated : s) })}
              onDelete={() => mark({ ...cfg, sections: cfg.sections.filter((_, i) => i !== idx) })}
            />
          ))}
          <button onClick={addSection}
            className="flex items-center gap-2 text-sm font-semibold text-orange-600 border-2 border-dashed border-orange-300 hover:border-orange-400 bg-orange-50 hover:bg-orange-100 px-4 py-3 rounded-xl w-full justify-center transition-colors">
            <Plus className="w-4 h-4" /> Add Section
          </button>
        </div>
      )}

      {/* Quick Actions Tab */}
      {activeTab === "quick" && (
        <div className="space-y-3 max-w-xl">
          <p className="text-xs text-slate-400">These 4 items appear as a 2×2 grid for logged-in users at the top of the mobile account page.</p>
          {(cfg.quick_actions || []).map((item, idx) => (
            <ItemRow
              key={item.id || idx}
              item={item}
              onUpdate={updated => mark({ ...cfg, quick_actions: cfg.quick_actions.map((it, i) => i === idx ? updated : it) })}
              onDelete={() => mark({ ...cfg, quick_actions: cfg.quick_actions.filter((_, i) => i !== idx) })}
            />
          ))}
          <button onClick={() => mark({ ...cfg, quick_actions: [...(cfg.quick_actions || []), { id: `qa_${Date.now()}`, label: "New Action", icon: "Star", url: "#", tab: "", enabled: true }] })}
            className="flex items-center gap-2 text-sm font-semibold text-orange-600 border-2 border-dashed border-orange-300 hover:border-orange-400 bg-orange-50 hover:bg-orange-100 px-4 py-3 rounded-xl w-full justify-center transition-colors">
            <Plus className="w-4 h-4" /> Add Quick Action
          </button>
        </div>
      )}

      {/* Login Prompt Tab */}
      {activeTab === "login_prompt" && (
        <div className="max-w-lg bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <p className="text-xs text-slate-400">Shown at the top of the account page when the user is not logged in.</p>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Prompt Text</label>
            <input value={cfg.login_prompt?.text || ""} onChange={e => mark({ ...cfg, login_prompt: { ...cfg.login_prompt, text: e.target.value } })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400" data-testid="login-prompt-text-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Button Text</label>
            <input value={cfg.login_prompt?.button_text || ""} onChange={e => mark({ ...cfg, login_prompt: { ...cfg.login_prompt, button_text: e.target.value } })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400" data-testid="login-prompt-btn-input" />
          </div>
        </div>
      )}

      {/* Login Page Text Tab */}
      {activeTab === "login_page" && (
        <div className="max-w-lg bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <p className="text-xs text-slate-400">Text shown on the mobile login screen.</p>
          {[
            { field: "title",      label: "Page Title",     testid: "login-page-title-input",    placeholder: "Log in for the best experience" },
            { field: "subtitle",   label: "Subtitle",       testid: "login-page-subtitle-input", placeholder: "Enter your email address to continue" },
            { field: "cta_text",   label: "CTA Button Text",testid: "login-page-cta-input",      placeholder: "Continue" },
          ].map(({ field, label, testid, placeholder }) => (
            <div key={field}>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">{label}</label>
              <input value={cfg.login_page?.[field] || ""} onChange={e => mark({ ...cfg, login_page: { ...cfg.login_page, [field]: e.target.value } })}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400" data-testid={testid} />
            </div>
          ))}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5 block">Terms & Privacy Text</label>
            <textarea value={cfg.login_page?.terms_text || ""} onChange={e => mark({ ...cfg, login_page: { ...cfg.login_page, terms_text: e.target.value } })}
              rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-orange-400 resize-none" data-testid="login-page-terms-input" />
          </div>
        </div>
      )}
    </div>
  );
}
